import React, { useEffect, useMemo, useState } from "react";
import { FiArrowRight, FiSearch } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { Product } from "../../interfaces/products/product";
import { getProducts, searchProductsLocally } from "../../data-access/apis/products/productApi";
import Styles from "../../styles/common/searchbox.module.css";

const SearchBox: React.FC = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [products, setProducts] = useState<Product[]>([]);

  useEffect(() => {
    let active = true;

    getProducts()
      .then((data) => {
        if (active) setProducts(data.filter((product) => product.isActive));
      })
      .catch(() => {
        if (active) setProducts([]);
      });

    return () => {
      active = false;
    };
  }, []);

  const suggestions = useMemo(
    () => (query.trim() ? searchProductsLocally(products, query).slice(0, 5) : []),
    [products, query],
  );

  const submit = (event: React.FormEvent) => {
    event.preventDefault();

    const value = query.trim();

    if (!value) {
      navigate("/products");
      return;
    }

    navigate(`/products?q=${encodeURIComponent(value)}`);
  };

  const selectSuggestion = (product: Product) => {
    navigate(`/product/${product.slug}`);
  };

  return (
    <div className={Styles.searchShell}>
      <form className={Styles.searchForm} onSubmit={submit}>
        <FiSearch className={Styles.searchIcon} aria-hidden="true" />

        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          className={Styles.searchInput}
          placeholder="Search products, brands, categories or describe what you need…"
          aria-label="Search products"
          autoComplete="off"
        />

        <button type="submit" className={Styles.searchButton} aria-label="Search products">
          <FiSearch />
          <span>Search</span>
          <FiArrowRight />
        </button>
      </form>

      {query.trim() && suggestions.length > 0 && (
        <div className={Styles.suggestions} role="listbox" aria-label="Search suggestions">
          {suggestions.map((product) => (
            <button
              type="button"
              key={product._id}
              className={Styles.suggestion}
              onClick={() => selectSuggestion(product)}
            >
              <span className={Styles.suggestionImage}>
                {product.images?.[0] ? (
                  <img src={product.images[0]} alt="" loading="lazy" />
                ) : (
                  <FiSearch />
                )}
              </span>
              <span className={Styles.suggestionText}>
                <strong>{product.title}</strong>
                <small>{product.category}{product.subCategory ? ` · ${product.subCategory}` : ""}</small>
              </span>
              <FiArrowRight className={Styles.suggestionArrow} />
            </button>
          ))}
          <button type="button" className={Styles.viewAll} onClick={submit}>
            View all search results
            <FiArrowRight />
          </button>
        </div>
      )}
    </div>
  );
};

export default SearchBox;
